import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { sanitarywareService, accountService } from "../../../services/enhancedCrudService";
import { toast } from "react-toastify";
import Select from "react-select";

const SanitarywareCreate = () => {
  const navigate = useNavigate();
  const { product_id } = useParams();
  const [productId, setProductId] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [typesLoading, setTypesLoading] = useState(false);
  const [attributesLoading, setAttributesLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [types, setTypes] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    manufacturing_company_name: "",
    description: "",
    sanitaryware_type_id: "",
    status: "active",
    published: false,
    specifications: {},
    images: [],
    seller_place: "",
    glaze: "",
    color: "",
    logo: "",
    weight_kg: "",
    size: "",
    loading_address: "",
    quantity: "",
    packing: "",
    grade: "",
    hidden_district_ids: [],
    hidden_district_names: [],
  });

  const SELLER_PLACE_CHOICES = [
    { value: "morbi", label: "Morbi" },
    { value: "than", label: "Than" },
    { value: "imported", label: "Imported" },
  ];

  const GLAZE_CHOICES = [
    { value: "glossy", label: "Glossy" },
    { value: "matt", label: "Matt" },
  ];

  const COLOR_CHOICES = [
    { value: "white", label: "White" },
    { value: "ivory", label: "Ivory" },
    { value: "plain", label: "Other Plain Color" },
    { value: "golden", label: "Golden" },
    { value: "silver", label: "Silver" },
    { value: "marble", label: "Marble" },
    { value: "wooden", label: "Wooden" },
    { value: "designer", label: "Designer/Multi Color" },
  ];

  const LOGO_CHOICES = [
    { value: "branded", label: "Brand Logo" },
    { value: "non_branded", label: "Without Logo" },
  ];

  const PACKING_CHOICES = [
    { value: "box_branded", label: "Box (With Brand)" },
    { value: "box_plain", label: "Box (Without Brand)" },
    { value: "grass/paper", label: "Grass/Paper Packing" },
    { value: "loose", label: "Without Box" },
  ];

  const GRADE_CHOICES = [
    { value: "premium", label: "Premium" },
    { value: "otp", label: "One Time Premium" },
    { value: "std_ii", label: "STD-II" },
    { value: "com_iii", label: "Com-III" },
    { value: "eco_iv", label: "Reject/DD (Eco-IV)" },
    { value: "rok", label: "ROK" },
  ];

  const fetchTypes = async () => {
    // Always start with loading state
    setTypesLoading(true);
    
    // Check if types are already cached in localStorage
    const cachedTypes = localStorage.getItem('sanitaryware_types');
    const cacheTimestamp = localStorage.getItem('sanitaryware_types_timestamp');
    const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    
    // If cached data exists and is not expired, use it
    if (cachedTypes && cacheTimestamp) {
      const now = Date.now();
      const cacheAge = now - parseInt(cacheTimestamp);
      
      if (cacheAge < CACHE_DURATION) {
        try {
          const parsedTypes = JSON.parse(cachedTypes);
          setTypes(parsedTypes);
          setTypesLoading(false);
          return;
        } catch (error) {
          console.error("Error parsing cached types:", error);
          // If parsing fails, continue to fetch from API
        }
      }
    }
    
    // If no cache or cache expired, fetch from API
    try {
      const response = await sanitarywareService.getTypes();
      let formattedTypes = response.types.map((type) => {
        return {
          value: type.id,
          label: type.name,
        };
      });
      setTypes(formattedTypes);
      
      // Cache the types data
      localStorage.setItem('sanitaryware_types', JSON.stringify(formattedTypes));
      localStorage.setItem('sanitaryware_types_timestamp', Date.now().toString());
    } catch (error) {
      console.error("Error fetching types:", error);
      toast.error("Failed to load product types. Please refresh the page.");
    } finally {
      setTypesLoading(false);
    }
  };

  const fetchAttributes = async () => {
    if (!formData.sanitaryware_type_id) {
      setAttributes([]);
      return;
    }
    
    try {
      setAttributesLoading(true);
      const response = await sanitarywareService.getAttributes({
        type: formData.sanitaryware_type_id,
      });
      let formattedAttributes = [];
      if (response.filters) {
        formattedAttributes = response.filters.map((filter) => {
          let formattedValues = filter.values.map((value) => {
            return {
              value: value.value_id,
              label: value.value_name,
            };
          });
          return {
            value: filter.attribute_id,
            label: filter.attribute_name,
            values: formattedValues,
            is_required: filter.is_required,
          };
        });
      } else if (response.attributes) {
        formattedAttributes = response.attributes.map((attribute) => {
          let formattedValues = attribute.values.map((value) => {
            return {
              value: value.id,
              label: value.display_name,
            };
          });
          return {
            value: attribute.id,
            label: attribute.display_name,
            values: formattedValues,
            is_required: attribute.is_required,
          };
        });
      }
      setAttributes(formattedAttributes);
    } catch (error) {
      console.error("Error fetching attributes:", error);
      toast.error("Failed to load product attributes. Please try again.");
    } finally {
      setAttributesLoading(false);
    }
  };

  const fetchDistricts = async () => {
    setDistrictsLoading(true);
    const cachedDistricts = localStorage.getItem('districts');
    const cacheTimestamp = localStorage.getItem('districts_timestamp');
    const CACHE_DURATION = 24 * 60 * 60 * 1000;
        if (cachedDistricts && cacheTimestamp) {
      const now = Date.now();
      const cacheAge = now - parseInt(cacheTimestamp);
      
      if (cacheAge < CACHE_DURATION) {
        try {
          const parsedDistricts = JSON.parse(cachedDistricts);
          setDistricts(parsedDistricts);
          setDistrictsLoading(false);
          return;
        } catch (error) {
          console.error("Error parsing cached districts:", error);
        }
      }
    }
    
    try {
      const response = await accountService.getAllDistricts();
      if (response && response.length) {
        let mapDistricts = response.map((row) => {
          return {
            label: row.name,
            value: row.id,
          };
        });
        setDistricts(mapDistricts);
        localStorage.setItem('districts', JSON.stringify(mapDistricts));
        localStorage.setItem('districts_timestamp', Date.now().toString());
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
      toast.error("Failed to load districts. Please refresh the page.");
    } finally {
      setDistrictsLoading(false);
    }
  };

  const fetchSanitarywareDetails = async () => {
    if (!product_id) return;
    
    try {
      setDetailsLoading(true);
      const response = await sanitarywareService.getDetail(product_id);
      if (response) {
        const specifications = {};
        response.attributes.forEach((attribute) => {
          specifications[attribute.attribute_id] =
            attribute.value_id.toString();
        });
        const hiddenDistrictIds = response.hidden_districts || response.hidden_district_ids || [];
        const hiddenDistrictNames = response.hidden_district_names || [];
        
        setFormData({
          name: response.name,
          manufacturing_company_name: response.manufacturing_company_name || "",
          description: response.description,
          sanitaryware_type_id: response.type,
          status: response.status,
          published: response.published,
          specifications: specifications,
          images: response.images,
          seller_place: response.seller_place,
          glaze: response.glaze,
          color: response.color,
          logo: response.logo,
          weight_kg: response.weight_kg || "",
          size: response.size || "",
          loading_address: response.loading_address || "",
          quantity: response.quantity || "",
          packing: response.packing,
          grade: response.grade,
          hidden_district_ids: hiddenDistrictIds,
          hidden_district_names: hiddenDistrictNames,
        });
      }
    } catch (error) {
      console.error("Error fetching sanitaryware details:", error);
      toast.error("Failed to load product details. Please refresh the page.");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const selected_attributes = [];
      Object.entries(formData.specifications).forEach(([key, value]) => {
        selected_attributes.push({
          attribute_id: parseInt(key),
          value_id: parseInt(value),
        });
      });

      const productData = {
        name: formData.name,
        manufacturing_company_name: formData.manufacturing_company_name,
        description: formData.description,
        category_id: 2,
        sanitaryware_type_id: parseInt(formData.sanitaryware_type_id),
        status: formData.status,
        published: formData.published,
        selected_attributes,
        seller_place: formData.seller_place,
        glaze: formData.glaze,
        color: formData.color,
        logo: formData.logo,
        weight_kg: parseFloat(formData.weight_kg) || 0,
        size: formData.size,
        loading_address: formData.loading_address || "",
        quantity: parseInt(formData.quantity) || 0,
        packing: formData.packing,
        grade: formData.grade,
        hidden_districts: Array.isArray(formData.hidden_district_ids) ? formData.hidden_district_ids : [],
      };

      if (product_id) {
        await sanitarywareService.update(product_id, productData);
        toast.success("Sanitaryware updated successfully!");
        navigate("/products/sanitaryware");
      } else {
        const response = await sanitarywareService.create(productData);
        toast.success("Sanitaryware created successfully!");
        // Redirect to images page after successful creation
        const newProductId = response.product_id || response.id || response.data?.product_id || response.data?.id;
        if (newProductId) {
          navigate(`/products/sanitaryware/${newProductId}/images`);
        } else {
          console.warn("Could not find product ID in response, redirecting to list");
          navigate("/products/sanitaryware");
        }
      }
    } catch (error) {
      let message = "Failed to save product. Please try again.";
      if (error.response && error.response.data) {
        message = error.response.data.error
          ? error.response.data.error
          : error.response.data.non_field_errors
            ? error.response.data.non_field_errors[0]
            : message;
      }
      toast.error(message);
      console.error("Error creating/updating product:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      setInitialLoading(true);
      try {
        const promises = [fetchTypes(), fetchDistricts()];
        if (product_id) {
          setProductId(product_id);
          promises.push(fetchSanitarywareDetails());
        }
        await Promise.all(promises);
      } catch (error) {
        console.error("Error initializing data:", error);
        toast.error("Failed to load initial data. Please refresh the page.");
      } finally {
        setInitialLoading(false);
      }
    };

    initializeData();
  }, [product_id]);

  useEffect(() => {
    if (formData.sanitaryware_type_id) {
      fetchAttributes();
    }
  }, [formData.sanitaryware_type_id]);

  if (initialLoading) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="mt-3">Loading product data...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">
              {product_id
                ? "Edit Sanitaryware Product"
                : "Create Sanitaryware Product"}
            </h4>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-12">
                  <div className="">
                    <div className="row">
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Product Name *</label>
                          <input
                            type="text"
                            className="form-control"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Manufacturing Company Name</label>
                          <input
                            type="text"
                            className="form-control"
                            name="manufacturing_company_name"
                            value={formData.manufacturing_company_name}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Seller Place *</label>
                          <Select
                            options={SELLER_PLACE_CHOICES}
                            value={SELLER_PLACE_CHOICES.find(
                              (opt) => opt.value === formData.seller_place
                            )}
                            onChange={(selected) =>
                              setFormData((prev) => ({
                                ...prev,
                                seller_place: selected?.value || "",
                              }))
                            }
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Glaze *</label>
                          <Select
                            options={GLAZE_CHOICES}
                            value={GLAZE_CHOICES.find(
                              (opt) => opt.value === formData.glaze
                            )}
                            onChange={(selected) =>
                              setFormData((prev) => ({
                                ...prev,
                                glaze: selected?.value || "",
                              }))
                            }
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Color *</label>
                          <Select
                            options={COLOR_CHOICES}
                            value={COLOR_CHOICES.find(
                              (opt) => opt.value === formData.color
                            )}
                            onChange={(selected) =>
                              setFormData((prev) => ({
                                ...prev,
                                color: selected?.value || "",
                              }))
                            }
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Logo *</label>
                          <Select
                            options={LOGO_CHOICES}
                            value={LOGO_CHOICES.find(
                              (opt) => opt.value === formData.logo
                            )}
                            onChange={(selected) =>
                              setFormData((prev) => ({
                                ...prev,
                                logo: selected?.value || "",
                              }))
                            }
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Weight (kg) *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            className="form-control"
                            name="weight_kg"
                            value={formData.weight_kg}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Size *</label>
                          <input
                            type="text"
                            className="form-control"
                            name="size"
                            value={formData.size}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Loading Address *</label>
                          <input
                            type="text"
                            className="form-control"
                            name="loading_address"
                            value={formData.loading_address}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Quantity *</label>
                          <input
                            type="number"
                            min="1"
                            className="form-control"
                            name="quantity"
                            value={formData.quantity}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Packing *</label>
                          <Select
                            options={PACKING_CHOICES}
                            value={PACKING_CHOICES.find(
                              (opt) => opt.value === formData.packing
                            )}
                            onChange={(selected) =>
                              setFormData((prev) => ({
                                ...prev,
                                packing: selected?.value || "",
                              }))
                            }
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Grade *</label>
                          <Select
                            options={GRADE_CHOICES}
                            value={GRADE_CHOICES.find(
                              (opt) => opt.value === formData.grade
                            )}
                            onChange={(selected) =>
                              setFormData((prev) => ({
                                ...prev,
                                grade: selected?.value || "",
                              }))
                            }
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Product Type *</label>
                          <Select
                            options={types}
                            value={types.find(
                              (opt) =>
                                opt.value == formData.sanitaryware_type_id
                            )}
                            onChange={(selected) =>
                              setFormData((prev) => ({
                                ...prev,
                                sanitaryware_type_id: selected?.value || "",
                              }))
                            }
                            placeholder={typesLoading ? "Loading types..." : "Select product type..."}
                            isLoading={typesLoading}
                            required
                          />
                        </div>
                      </div>
                      {attributesLoading && formData.sanitaryware_type_id ? (
                        <div className="col-md-12">
                          <div className="text-center py-3">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            <span className="ms-2">Loading attributes...</span>
                          </div>
                        </div>
                      ) : (
                        attributes.map((attr) => (
                          <div key={attr.value} className="col-md-3">
                            <div className="form-group">
                              <label>
                                {attr.label}{" "}
                                {attr.is_required && (
                                  <span className="text-danger">*</span>
                                )}
                              </label>
                              <Select
                                required={attr.is_required}
                                options={attr.values}
                                value={attr.values.find(
                                  (opt) =>
                                    opt.value ==
                                    formData.specifications[attr.value]
                                )}
                                onChange={(selected) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    specifications: {
                                      ...prev.specifications,
                                      [attr.value]: selected?.value || "",
                                    },
                                  }))
                                }
                              />
                            </div>
                          </div>
                        ))
                      )}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Districts where you want to hide your stock</label>
                          <Select
                            isMulti
                            options={districts}
                            value={districts.filter(district =>
                              formData.hidden_district_ids.includes(district.value)
                            )}
                            onChange={(selected) => {
                              const ids = selected ? selected.map(item => item.value) : [];
                              const names = selected ? selected.map(item => item.label) : [];
                              setFormData((prev) => ({
                                ...prev,
                                hidden_district_ids: ids,
                                hidden_district_names: names,
                              }));
                            }}
                            placeholder={districtsLoading ? "Loading districts..." : "Select districts to hide..."}
                            isLoading={districtsLoading}
                            isClearable
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Remark</label>
                        <textarea
                          className="form-control"
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          rows="4"
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="text-end">
                      <button
                        type="submit"
                        className="btn btn-primary btn-block me-2"
                        disabled={loading}
                      >
                        {loading ? "Saving..." : product_id ? "Update Product" : "Save And continue"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-block"
                        onClick={() => navigate("/products/sanitaryware")}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SanitarywareCreate;
